import React, { useState } from "react";
import { SketchPicker } from "react-color";

import "./Axis.css"; // Ensure you have a corresponding CSS file for styling

const Axis = ({ setChartConfig, axisConfig, axisType }) => {
  const {
    type,
    position,
    offsetY,
    labels,
    title,
    axisTicks,
    axisBorder,
    show,
    opposite,
    reversed,
    masking,
    min,
    max,
    tickAmount
  } = axisConfig;

  const [isLabelsOpen, setIsLabelsOpen] = useState(false);
  const [isTitleOpen, setIsTitleOpen] = useState(false);
  const [isTicksOpen, setIsTicksOpen] = useState(false);
  const [isBorderOpen, setIsBorderOpen] = useState(false);
  const [showLabelsColorPicker, setShowLabelsColorPicker] = useState(false);
  const [showTitleColorPicker, setShowTitleColorPicker] = useState(false);
  const [showTicksColorPicker, setShowTicksColorPicker] = useState(false);
  const [showBorderColorPicker, setShowBorderColorPicker] = useState(false);

  const handleAxisChange = (key, value) => {
    setChartConfig((prevConfig) => ({
      ...prevConfig,
      [axisType]: {
        ...prevConfig[axisType],
        [key]: value
      }
    }));
  };

  const handleLabelsChange = (key, value) => {
    setChartConfig((prevConfig) => ({
      ...prevConfig,
      [axisType]: {
        ...prevConfig[axisType],
        labels: {
          ...prevConfig[axisType].labels,
          [key]: value
        }
      }
    }));
  };

  const handleTitleChange = (key, value) => {
    setChartConfig((prevConfig) => ({
      ...prevConfig,
      [axisType]: {
        ...prevConfig[axisType],
        title: {
          ...prevConfig[axisType].title,
          [key]: value
        }
      }
    }));
  };

  const handleTicksChange = (key, value) => {
    setChartConfig((prevConfig) => ({
      ...prevConfig,
      [axisType]: {
        ...prevConfig[axisType],
        axisTicks: {
          ...prevConfig[axisType].axisTicks,
          [key]: value
        }
      }
    }));
  };

  const handleBorderChange = (key, value) => {
    setChartConfig((prevConfig) => ({
      ...prevConfig,
      [axisType]: {
        ...prevConfig[axisType],
        axisBorder: {
          ...prevConfig[axisType].axisBorder,
          [key]: value
        }
      }
    }));
  };

  return (
    <div className="axis-container">
      {/* Title with Dropdown Icon */}
      <h3 className="axis-head">{axisType === "xaxis" ? "X-Axis" : "Y-Axis"}</h3>

      {/* Type & Position Dropdowns (for X-axis only) */}
      {axisType === "xaxis" && (
        <div className="axis-dropdowns">
          <div>
            <label className="axis-label">Type</label>
            <select value={type} onChange={(e) => handleAxisChange("type", e.target.value)} className="axis-dropdown">
              <option value="datetime">datetime</option>
              <option value="category">category</option>
              <option value="numeric">numeric</option>
            </select>
          </div>

          <div>
            <label className="axis-label">Position</label>
            <select
              value={position}
              onChange={(e) => handleAxisChange("position", e.target.value)}
              className="axis-dropdown"
            >
              <option value="bottom">bottom</option>
              <option value="top">top</option>
            </select>
          </div>
        </div>
      )}

      {/* Offset Slider (for X-axis only) */}
      {axisType === "xaxis" && (
        <div className="axis-slider-container">
          <label className="axis-slider-label">Top Offset</label>
          <div className="axis-slider-controls">
            <input
              type="range"
              min="-50"
              max="50"
              value={offsetY}
              onChange={(e) => handleAxisChange("offsetY", Number(e.target.value))}
              className="axis-slider"
            />
          </div>
        </div>
      )}

      {/* General Settings (for both X and Y axis) */}
      <div className="axis-settings">
        {axisType === "yaxis" && (
          <>
            <div className="axis-section">
              <label className="axis-label">Show</label>
              <input type="checkbox" checked={show} onChange={(e) => handleAxisChange("show", e.target.checked)} />
            </div>

            <div className="axis-section">
              <label className="axis-label">Opposite</label>
              <input
                type="checkbox"
                checked={opposite}
                onChange={(e) => handleAxisChange("opposite", e.target.checked)}
              />
            </div>

            <div className="axis-section">
              <label className="axis-label">Reversed</label>
              <input
                type="checkbox"
                checked={reversed}
                onChange={(e) => handleAxisChange("reversed", e.target.checked)}
              />
            </div>

            <div className="axis-section">
              <label className="axis-label">Masking</label>
              <input
                type="text"
                value={masking || "{value}"}
                onChange={(e) => handleAxisChange("masking", e.target.value)}
              />
            </div>

            <div className="axis-section">
              <label className="axis-label">Left Offset</label>
              <input
                type="number"
                value={labels.offsetX || 0}
                onChange={(e) => handleLabelsChange("offsetX", Number(e.target.value))}
              />
            </div>

            <div className="axis-section">
              <label className="axis-label">Lowest Value</label>
              <input type="number" value={min} onChange={(e) => handleAxisChange("min", Number(e.target.value))} />
            </div>

            <div className="axis-section">
              <label className="axis-label">Highest Value</label>
              <input type="number" value={max} onChange={(e) => handleAxisChange("max", Number(e.target.value))} />
            </div>

            <div className="axis-section">
              <label className="axis-label">Tick Amount</label>
              <input
                type="number"
                value={tickAmount || 5}
                onChange={(e) => handleAxisChange("tickAmount", Number(e.target.value))}
              />
            </div>
          </>
        )}

        {/* Subsections (Buttons) */}
        <div className="axis-settings-buttons">
          {["Labels", "Title", "Ticks", "Border"].map((item) => (
            <React.Fragment key={item}>
              <button
                className="axis-settings-button"
                onClick={() => {
                  if (item === "Labels") {
                    setIsLabelsOpen(!isLabelsOpen);
                    setIsTitleOpen(false);
                    setIsTicksOpen(false);
                    setIsBorderOpen(false);
                  } else if (item === "Title") {
                    setIsTitleOpen(!isTitleOpen);
                    setIsLabelsOpen(false);
                    setIsTicksOpen(false);
                    setIsBorderOpen(false);
                  } else if (item === "Ticks") {
                    setIsTicksOpen(!isTicksOpen);
                    setIsLabelsOpen(false);
                    setIsTitleOpen(false);
                    setIsBorderOpen(false);
                  } else if (item === "Border") {
                    setIsBorderOpen(!isBorderOpen);
                    setIsLabelsOpen(false);
                    setIsTitleOpen(false);
                    setIsTicksOpen(false);
                  }
                }}
              >
                {axisType === "xaxis" ? `X-Axis ${item}` : `Y-Axis ${item}`}
                <span>➤</span>
              </button>

              {/* Labels Settings (Collapsible) */}
              {item === "Labels" && isLabelsOpen && (
                <div className="axis-labels-settings">
                  <div className="axis-labels-section">
                    <label className="axis-label">Show labels</label>
                    <input
                      type="checkbox"
                      checked={labels?.show || false}
                      onChange={(e) => handleLabelsChange("show", e.target.checked)}
                    />
                  </div>

                  <div className="color-picker-wrapper">
                    <label className="axis-label">Labels Color</label>
                    <button
                      className="color-picker-button"
                      onClick={() => setShowLabelsColorPicker(!showLabelsColorPicker)}
                    >
                      <div
                        className="color-swatch"
                        style={{
                          backgroundColor: labels?.style?.colors || "#000000"
                        }}
                      />
                    </button>
                    {showLabelsColorPicker && (
                      <div className="picker-popover">
                        <div className="picker-cover" onClick={() => setShowLabelsColorPicker(false)} />
                        <SketchPicker
                          color={labels?.style?.colors || "#000000"}
                          onChangeComplete={(color) =>
                            handleLabelsChange("style", {
                              ...labels.style,
                              colors: color.hex
                            })
                          }
                        />
                      </div>
                    )}
                  </div>

                  <div className="axis-labels-section">
                    <label className="axis-label">Font Size</label>
                    <input
                      type="number"
                      value={labels.style?.fontSize?.split("px")?.[0] || "12"}
                      onChange={(e) =>
                        handleLabelsChange("style", {
                          ...labels.style,
                          fontSize: `${e.target.value}px`
                        })
                      }
                    />
                  </div>

                  <div className="axis-labels-section">
                    <label className="axis-label">Font Weight</label>
                    <input
                      type="range"
                      min="100"
                      max="900"
                      step="100"
                      value={labels?.style?.fontWeight || "400"}
                      onChange={(e) =>
                        handleLabelsChange("style", {
                          ...labels.style,
                          fontWeight: e.target.value
                        })
                      }
                      className="font-weight-slider"
                    />
                  </div>

                  <div className="axis-labels-section">
                    <label className="axis-label">Rotate Labels</label>
                    <input
                      type="number"
                      value={labels?.rotate || 0}
                      onChange={(e) => handleLabelsChange("rotate", Number(e.target.value))}
                    />
                  </div>
                </div>
              )}

              {/* Title Settings (Collapsible) */}
              {item === "Title" && isTitleOpen && (
                <div className="axis-title-settings">
                  <div className="axis-title-section">
                    <label className="axis-label">Title Text</label>
                    <input
                      type="text"
                      value={title?.text || ""}
                      onChange={(e) => handleTitleChange("text", e.target.value)}
                      placeholder="Enter title text"
                    />
                  </div>

                  <div className="color-picker-wrapper">
                    <label className="axis-label">Title Color</label>
                    <button
                      className="color-picker-button"
                      onClick={() => setShowTitleColorPicker(!showTitleColorPicker)}
                    >
                      <div
                        className="color-swatch"
                        style={{
                          backgroundColor: title?.style?.color || "#000000"
                        }}
                      />
                    </button>
                    {showTitleColorPicker && (
                      <div className="picker-popover">
                        <div className="picker-cover" onClick={() => setShowTitleColorPicker(false)} />
                        <SketchPicker
                          color={title?.style?.color || "#000000"}
                          onChangeComplete={(color) =>
                            handleTitleChange("style", {
                              ...title.style,
                              color: color.hex
                            })
                          }
                        />
                      </div>
                    )}
                  </div>

                  <div className="axis-title-section">
                    <label className="axis-label">Title Font Size</label>
                    <input
                      type="number"
                      value={title?.style?.fontSize?.split("px")?.[0] || "11"}
                      onChange={(e) =>
                        handleTitleChange("style", {
                          ...title.style,
                          fontSize: `${e.target.value}px`
                        })
                      }
                    />
                  </div>

                  <div className="axis-title-section">
                    <label className="axis-label">Title Font Weight</label>
                    <input
                      type="range"
                      min="100"
                      max="900"
                      step="100"
                      value={title?.style?.fontWeight || "400"}
                      onChange={(e) =>
                        handleTitleChange("style", {
                          ...title.style,
                          fontWeight: e.target.value
                        })
                      }
                      className="font-weight-slider"
                    />
                  </div>

                  <div className="axis-title-section">
                    <label className="axis-label">Title Left Offset</label>
                    <input
                      type="number"
                      value={title?.offsetX || 0}
                      onChange={(e) => handleTitleChange("offsetX", Number(e.target.value))}
                    />
                  </div>

                  <div className="axis-title-section">
                    <label className="axis-label">Title Top Offset</label>
                    <input
                      type="number"
                      value={title?.offsetY || 0}
                      onChange={(e) => handleTitleChange("offsetY", Number(e.target.value))}
                    />
                  </div>
                </div>
              )}

              {/* Ticks Settings (Collapsible) */}
              {item === "Ticks" && isTicksOpen && (
                <div className="axis-ticks-settings">
                  <div className="axis-ticks-section">
                    <label className="axis-label">Show Ticks</label>
                    <input
                      type="checkbox"
                      checked={axisTicks?.show || false}
                      onChange={(e) => handleTicksChange("show", e.target.checked)}
                    />
                  </div>

                  <div className="color-picker-wrapper">
                    <label className="axis-label">Ticks Color</label>
                    <button
                      className="color-picker-button"
                      onClick={() => setShowTicksColorPicker(!showTicksColorPicker)}
                    >
                      <div
                        className="color-swatch"
                        style={{
                          backgroundColor: axisTicks?.color || "#000000"
                        }}
                      />
                    </button>
                    {showTicksColorPicker && (
                      <div className="picker-popover">
                        <div className="picker-cover" onClick={() => setShowTicksColorPicker(false)} />
                        <SketchPicker
                          color={axisTicks?.color || "#000000"}
                          onChangeComplete={(color) => handleTicksChange("color", color.hex)}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Border Settings (Collapsible) */}
              {item === "Border" && isBorderOpen && (
                <div className="axis-border-settings">
                  <div className="axis-border-section">
                    <label className="axis-label">Show Border</label>
                    <input
                      type="checkbox"
                      checked={axisBorder?.show || false}
                      onChange={(e) => handleBorderChange("show", e.target.checked)}
                    />
                  </div>

                  <div className="color-picker-wrapper">
                    <label className="axis-label">Border Color</label>
                    <button
                      className="color-picker-button"
                      onClick={() => setShowBorderColorPicker(!showBorderColorPicker)}
                    >
                      <div
                        className="color-swatch"
                        style={{
                          backgroundColor: axisBorder?.color || "#000000"
                        }}
                      />
                    </button>
                    {showBorderColorPicker && (
                      <div className="picker-popover">
                        <div className="picker-cover" onClick={() => setShowBorderColorPicker(false)} />
                        <SketchPicker
                          color={axisBorder?.color || "#000000"}
                          onChangeComplete={(color) => handleBorderChange("color", color.hex)}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Axis;
